import * as configActions from '../actions/config';
import * as userActions from '../actions/user';
import normalizeReferenceCountries from '../../utils/referenceCountries';

function normalizeCurrencyList(raw) {
  if (!Array.isArray(raw)) return null;
  return raw.map((code) => String(code).toUpperCase()).filter(Boolean);
}

function normalizeSupportedOffRampCurrencies(userConfig) {
  const raw =
    userConfig?.offRampCurrencies ??
    userConfig?.OffRampCurrencies ??
    userConfig?.supportedCurrencies ??
    userConfig?.SupportedCurrencies;
  return normalizeCurrencyList(raw);
}

function normalizeSupportedOnRampCurrencies(userConfig) {
  const raw = userConfig?.onRampCurrencies ?? userConfig?.OnRampCurrencies;
  return normalizeCurrencyList(raw);
}

const defaultState = {
  currencies: [],
  countries: [],
  allowedOnboardingCountries: [],
  prohibitedOperatingCountries: [],
  supportedOffRampCurrencies: null,
  supportedOnRampCurrencies: null,
  referenceCountries: [],
  referenceCountriesLoading: false,
  referenceCountriesError: null,
  loading: false,
  currencyConfigs: {},
};

function normalizeConfigCountries(countries) {
  if (!Array.isArray(countries)) return [];
  const seen = new Set();
  return countries
    .map((country) => ({
      code: String(country?.code ?? country?.Code ?? '').toUpperCase(),
      name: country?.name ?? country?.Name ?? country?.code ?? country?.Code ?? '',
      flag: country?.flag ?? country?.Flag ?? null,
    }))
    .filter((country) => {
      if (!country.code || seen.has(country.code)) return false;
      seen.add(country.code);
      return true;
    });
}

// eslint-disable-next-line default-param-last
const config = (state = defaultState, action) => {
  switch (action.type) {
    case configActions.GET_CONFIG: {
      return {
        ...state,
        loading: true,
      };
    }
    case configActions.GET_CONFIG_SUCCESS: {
      const { payload } = action;
      const currencies = payload.offRampConfig?.currencies ?? payload.currencies ?? [];
      const complianceConfig = payload.complianceConfig ?? payload.ComplianceConfig ?? {};
      const allowedOnboardingCountries = normalizeConfigCountries(
        complianceConfig.allowedOnboardingCountries ?? complianceConfig.AllowedOnboardingCountries,
      );
      const prohibitedOperatingCountries = normalizeConfigCountries(
        complianceConfig.prohibitedOperatingCountries ?? complianceConfig.ProhibitedOperatingCountries,
      );
      return {
        ...state,
        currencies,
        countries: allowedOnboardingCountries,
        allowedOnboardingCountries,
        prohibitedOperatingCountries,
        loading: false,
      };
    }
    case configActions.GET_CONFIG_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case configActions.GET_CURRENCY_CONFIG: {
      return {
        ...state,
        loadingCurrencyConfig: true,
      };
    }
    case configActions.GET_CURRENCY_CONFIG_SUCCESS: {
      const { currencyCode, data } = action.payload;
      return {
        ...state,
        currencyConfigs: {
          ...state.currencyConfigs,
          [currencyCode]: data,
        },
        loadingCurrencyConfig: false,
      };
    }
    case configActions.GET_CURRENCY_CONFIG_ERROR:
      return {
        ...state,
        loadingCurrencyConfig: false,
        currencyConfigError: action.payload,
      };
    case configActions.GET_REFERENCE_COUNTRIES:
      return {
        ...state,
        referenceCountriesLoading: true,
        referenceCountriesError: null,
      };
    case configActions.GET_REFERENCE_COUNTRIES_SUCCESS:
      return {
        ...state,
        referenceCountriesLoading: false,
        referenceCountries: normalizeReferenceCountries(action.payload?.countries ?? action.payload ?? []),
        referenceCountriesError: null,
      };
    case configActions.GET_REFERENCE_COUNTRIES_ERROR:
      return {
        ...state,
        referenceCountriesLoading: false,
        referenceCountriesError: action.payload,
      };
    case configActions.GET_USER_CONFIG_SUCCESS:
      return {
        ...state,
        supportedOffRampCurrencies: normalizeSupportedOffRampCurrencies(action.payload),
        supportedOnRampCurrencies: normalizeSupportedOnRampCurrencies(action.payload),
      };
    case userActions.CLEAR_USER:
      return {
        ...state,
        supportedOffRampCurrencies: null,
        supportedOnRampCurrencies: null,
      };
    default:
      return state;
  }
};

export default config;
